import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Token management
export const tokenManager = {
  getToken: (): string | null => {
    return localStorage.getItem('auth_token');
  },

  setToken: (token: string): void => {
    try {
      console.log('tokenManager.setToken: About to store token');
      console.log('localStorage before:', localStorage.getItem('auth_token'));

      localStorage.setItem('auth_token', token);

      console.log('localStorage after setItem:', localStorage.getItem('auth_token'));
      console.log('localStorage length:', localStorage.length);
      console.log('localStorage keys:', Object.keys(localStorage));

      // Try alternative approach
      window.localStorage.setItem('auth_token_test', token);
      console.log('window.localStorage test:', window.localStorage.getItem('auth_token_test'));

    } catch (error) {
      console.error('localStorage.setItem failed:', error);
    }
  },

  removeToken: (): void => {
    localStorage.removeItem('auth_token');
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';

      // Only remove token for auth-related endpoints, not for general API calls
      // that might fail due to missing/invalid tokens
      if (url.includes('/auth/me') || url.includes('/auth/logout')) {
        tokenManager.removeToken();
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface StreamingOption {
  name: string;
  type: 'subscription' | 'rent' | 'buy';
  quality?: string;
  price?: string;
  link?: string;
}

export interface StreamingData {
  streaming_options: StreamingOption[];
  tmdb_id?: number;
  imdb_id?: string;
  error?: string;
}

export interface RecommendationExplanation {
  shared_features?: string[];
  similarities?: {
    content?: number;
    script?: number;
    video?: number;
    audio?: number;
  };
  hybrid_score?: number;
  weights?: {
    content?: number;
    script?: number;
    video?: number;
    audio?: number;
  };
}

export interface Movie {
  id: number;
  filename: string;
  cleaned_title: string;
  omdb_title?: string;
  year?: number;
  imdb_id?: string;
  imdb_rating?: string;
  rotten_tomatoes?: string;
  metacritic?: string;
  director?: string;
  actors?: string;
  runtime?: string;
  rated?: string;
  box_office?: string;
  awards?: string;
  plot?: string;
  genres?: string[];
  directors?: string[];
  similarity_score?: number;
  streaming_data?: StreamingData;
  explanation?: RecommendationExplanation;
  shared_features?: string[];
}

export interface Genre {
  name: string;
  movie_count: number;
}

export interface Director {
  name: string;
  movie_count: number;
}

export interface SearchResponse {
  movies: Movie[];
  count: number;
}

export interface RateLimit {
  daily_limit: number;
  current_count?: number;
  remaining: number;
  reset_time: string;
  limit_exceeded?: boolean;
}

export interface User {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: User;
  user_id?: number;
  message?: string;
  error?: string;
  email_verification_required?: boolean;
}

export interface UserPreferences {
  favorite_genres: string[];
  favorite_directors: string[];
  favorite_actors: string[];
  min_rating: number;
  max_runtime?: number;
  preferred_decades: string[];
  weight_content?: number;
  weight_script?: number;
  weight_video?: number;
  weight_audio?: number;
}

export interface WatchlistItem extends Movie {
  watchlist_added_at: string;
  watchlist_watched: boolean;
  watchlist_watched_at?: string;
}

export interface RecommendationsResponse {
  recommendations: Movie[];
  count: number;
  movie_id: number;
  method?: string;
  user?: User;
  rate_limit?: RateLimit;
}

export interface GenresResponse {
  genres: Genre[];
  count: number;
}

export interface DirectorsResponse {
  directors: Director[];
  count: number;
}

export interface Actor {
  name: string;
  movie_count: number;
}

export interface ActorsResponse {
  actors: Actor[];
  count: number;
}

export interface MoviesResponse {
  movies: Movie[];
  count: number;
  genre?: string;
  director?: string;
}

export interface ApiStats {
  total_movies: number;
  total_genres: number;
  total_directors: number;
  movies_with_imdb: number;
  movies_with_rt: number;
  movies_with_metacritic: number;
  word2vec_loaded: boolean;
  vocab_size?: number;
  embedding_dim?: number;
  script_embeddings?: number;
}

export interface HealthResponse {
  status: string;
  word2vec_loaded: boolean;
  timestamp: string;
}

// API Functions
export const movieApi = {
  // Search movies
  searchMovies: async (query: string, limit: number = 10): Promise<SearchResponse> => {
    const response = await api.get(`/movies/search`, {
      params: { q: query, limit }
    });
    return response.data;
  },

  // Search movies by actor
  searchMoviesByActor: async (actor: string, limit: number = 10): Promise<SearchResponse> => {
    const response = await api.get(`/movies/search/actor`, {
      params: { q: actor, limit }
    });
    return response.data;
  },

  // Get movie by ID
  getMovie: async (id: number): Promise<Movie> => {
    const response = await api.get(`/movies/${id}`);
    return response.data;
  },

  // Get movie recommendations
  getRecommendations: async (movieId: number, topK: number = 5): Promise<RecommendationsResponse> => {
    const response = await api.get(`/movies/${movieId}/recommendations`, {
      params: { top_k: topK }
    });
    return response.data;
  },

  // Get hybrid movie recommendations
  getHybridRecommendations: async (
    movieId: number,
    topK: number = 5,
    weights?: { content: number; script: number; video: number; audio: number }
  ): Promise<RecommendationsResponse> => {
    const params: Record<string, unknown> = { top_k: topK };
    if (weights) {
      params.w_content = weights.content;
      params.w_script = weights.script;
      params.w_video = weights.video;
      params.w_audio = weights.audio;
    }
    const response = await api.get(`/movies/${movieId}/recommendations/hybrid`, { params });
    return response.data;
  },

  // Get random movie
  getRandomMovie: async (): Promise<Movie> => {
    const response = await api.get('/movies/random');
    return response.data;
  },

  // Get all genres
  getGenres: async (): Promise<GenresResponse> => {
    const response = await api.get('/genres');
    return response.data;
  },

  // Get movies by genre
  getMoviesByGenre: async (genre: string): Promise<MoviesResponse> => {
    const response = await api.get(`/genres/${encodeURIComponent(genre)}/movies`);
    return response.data;
  },

  // Get all directors
  getDirectors: async (): Promise<DirectorsResponse> => {
    const response = await api.get('/directors');
    return response.data;
  },

  // Get all actors
  getActors: async (): Promise<ActorsResponse> => {
    const response = await api.get('/actors');
    return response.data;
  },

  // Get movies by director
  getMoviesByDirector: async (director: string): Promise<MoviesResponse> => {
    const response = await api.get(`/directors/${encodeURIComponent(director)}/movies`);
    return response.data;
  },

  // Get API statistics
  getStats: async (): Promise<ApiStats> => {
    const response = await api.get('/stats');
    return response.data;
  },

  // Get streaming availability for a movie
  getMovieStreaming: async (movieId: number): Promise<StreamingData> => {
    const response = await api.get(`/movies/${movieId}/streaming`);
    return response.data.streaming_data;
  },

  // Get rate limit status for recommendations
  getRecommendationRateLimit: async (): Promise<{ rate_limit: RateLimit }> => {
    const response = await api.get('/rate-limit/recommendations');
    return response.data;
  },

  // Authentication
  register: async (userData: {
    username: string;
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
  }): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  login: async (credentials: {
    username: string;
    password: string;
  }): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  logout: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  verifyEmail: async (token: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  resendVerification: async (email: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  getCurrentUser: async (): Promise<{ user: User }> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // User Preferences
  getUserPreferences: async (): Promise<{ preferences: UserPreferences }> => {
    const response = await api.get('/user/preferences');
    return response.data;
  },

  updateUserPreferences: async (preferences: UserPreferences): Promise<{ success: boolean; message: string }> => {
    const response = await api.put('/user/preferences', preferences);
    return response.data;
  },

  // Watchlist
  getWatchlist: async (watched?: boolean): Promise<{ watchlist: WatchlistItem[]; count: number }> => {
    const params = watched !== undefined ? { watched: watched.toString() } : {};
    const response = await api.get('/user/watchlist', { params });
    return response.data;
  },

  addToWatchlist: async (movieId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/user/watchlist/${movieId}`);
    return response.data;
  },

  removeFromWatchlist: async (movieId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/user/watchlist/${movieId}`);
    return response.data;
  },

  markAsWatched: async (movieId: number, watched: boolean = true): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/user/watchlist/${movieId}/watched`, { watched });
    return response.data;
  },

  // Health check
  healthCheck: async (): Promise<HealthResponse> => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Helper functions
export const formatRating = (rating?: string): string => {
  if (!rating) return 'N/A';
  return rating;
};

export const formatYear = (year?: number): string => {
  if (!year) return 'Unknown';
  return year.toString();
};

export const formatRuntime = (runtime?: string): string => {
  if (!runtime) return 'Unknown';
  return runtime;
};

export const formatDirector = (director?: string): string => {
  if (!director) return 'Unknown';
  return director;
};

export const formatSimilarity = (score?: number): string => {
  if (!score) return '0%';
  return `${(score * 100).toFixed(1)}%`;
};

// Error handling
export const handleApiError = (error: any): string => {
  if (error.response?.status === 429) {
    // Rate limit exceeded
    const message = error.response?.data?.message || 'Rate limit exceeded';
    return message;
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export const isRateLimitError = (error: any): boolean => {
  return error.response?.status === 429;
};

export const isEmailNotVerifiedError = (error: any): boolean => {
  return error.response?.status === 403 && error.response?.data?.email_verification_required === true;
};

export const getRateLimitFromError = (error: any): RateLimit | null => {
  return error.response?.data?.rate_limit || null;
};

export default movieApi;
